'use client';

import React, { useState, useEffect } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { createClient } from '@/lib/supabase/client';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface EventDetails {
  event_date: string | null;
  gates_open: string | null;
  event_finish: string | null;
}

interface SiteSettings {
  show_countdown: boolean;
}

function getTimeLeft(eventDateStr: string): TimeLeft {
  const eventDate = new Date(eventDateStr);
  const now = new Date();
  const diff = eventDate.getTime() - now.getTime();

  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds };
}

function buildEventDateStr(date: string | null, time: string | null): string {
  if (date && time) {
    // time is stored as HH:MM:SS, combine with date
    return `${date}T${time}+01:00`;
  }
  // fallback to hardcoded date
  return '2026-06-13T11:00:00+01:00';
}

export default function CountdownBanner() {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isLive, setIsLive] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showCountdown, setShowCountdown] = useState(true);
  const [eventDetails, setEventDetails] = useState<EventDetails | null>(null);

  useEffect(() => {
    setMounted(true);
    const supabase = createClient();

    // Initial fetch of event_details and site_settings
    const fetchData = async () => {
      const results = await Promise.all([
        supabase.from('event_details').select('event_date, gates_open, event_finish').single(),
        supabase.from('site_settings').select('show_countdown').single(),
      ]);
      const evData = results[0].data;
      const settingsData = results[1].data;
      if (evData) setEventDetails(evData);
      if (settingsData) setShowCountdown(settingsData.show_countdown ?? true);
    };
    fetchData();

    // Real-time subscription for event_details changes
    const eventChannel = supabase
      .channel('realtime:event_details')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'event_details' }, (payload) => {
        const row = (payload.new || payload.old) as EventDetails;
        if (row) setEventDetails(row);
      })
      .subscribe();

    // Real-time subscription for site_settings changes (show_countdown toggle)
    const settingsChannel = supabase
      .channel('realtime:site_settings')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'site_settings' }, (payload) => {
        const row = payload.new as SiteSettings;
        if (row && typeof row.show_countdown === 'boolean') {
          setShowCountdown(row.show_countdown);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(eventChannel);
      supabase.removeChannel(settingsChannel);
    };
  }, []);

  // Countdown tick
  useEffect(() => {
    if (!mounted) return;

    const eventDateStr = buildEventDateStr(
      eventDetails?.event_date ?? null,
      eventDetails?.gates_open ?? null
    );
    const eventFinishStr = eventDetails?.event_finish
      ? buildEventDateStr(eventDetails.event_date ?? null, eventDetails.event_finish)
      : buildEventDateStr(null, null).replace('11:00', '16:30');

    const tick = () => {
      const now = new Date();
      const eventStart = new Date(eventDateStr);
      const eventEnd = new Date(eventFinishStr);

      if (now >= eventStart && now <= eventEnd) {
        setIsLive(true);
      } else {
        setIsLive(false);
        setTimeLeft(getTimeLeft(eventDateStr));
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [mounted, eventDetails]);

  if (!mounted) return null;
  if (!showCountdown) return null;

  if (isLive) {
    return (
      <div className="mx-4 -mt-4 relative z-10">
        <div className="gradient-primary rounded-2xl p-4 card-shadow-lg max-w-lg mx-auto">
          <div className="flex items-center justify-center gap-3">
            <div className="live-dot" />
            <span className="text-secondary font-bold text-lg">Event is LIVE NOW!</span>
            <div className="live-dot" />
          </div>
          <p className="text-secondary/70 text-center text-xs mt-1 font-medium">
            New Milton RFC • Gates are OPEN
          </p>
        </div>
      </div>
    );
  }

  const hasEnded = timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0;

  if (hasEnded) {
    return (
      <div className="mx-4 -mt-4 relative z-10">
        <div className="bg-muted rounded-2xl p-4 card-shadow max-w-lg mx-auto text-center">
          <p className="text-muted-foreground font-semibold">See you there! 🎉</p>
        </div>
      </div>
    );
  }

  const units = [
    { label: 'Days', value: timeLeft.days, key: 'cd-days' },
    { label: 'Hours', value: timeLeft.hours, key: 'cd-hours' },
    { label: 'Mins', value: timeLeft.minutes, key: 'cd-mins' },
    { label: 'Secs', value: timeLeft.seconds, key: 'cd-secs' },
  ];

  return (
    <div className="mx-4 -mt-4 relative z-10 max-w-lg mx-auto">
      <div className="bg-secondary rounded-2xl p-4 card-shadow-lg border border-primary/20">
        <div className="flex items-center justify-center gap-2 mb-3">
          <ClockIcon className="w-3.5 h-3.5 text-primary" />
          <span className="text-white/70 text-xs font-semibold uppercase tracking-widest">
            Event Starts In
          </span>
        </div>
        <div className="flex items-center justify-center gap-2">
          {units.map((unit, idx) => (
            <React.Fragment key={unit.key}>
              <div className="text-center">
                <div className="bg-white/10 rounded-xl px-3 py-2 min-w-[52px]">
                  <span className="countdown-number text-2xl font-bold text-primary block">
                    {String(unit.value).padStart(2, '0')}
                  </span>
                </div>
                <span className="text-white/50 text-[9px] font-semibold uppercase tracking-wider mt-1 block">
                  {unit.label}
                </span>
              </div>
              {idx < 3 && (
                <span className="text-primary/60 text-xl font-bold mb-4">:</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}